/**
 * Who is signed in.
 *
 * `GET /api/v1/me` is the single source of truth, so the app makes one request to answer both
 * "is there a session" and "who is it". allauth's own session endpoint would answer the first
 * but not the second, and the pending-verification state it reports is only needed right after
 * a sign-up or sign-in attempt, when the mutation's own error carries it.
 *
 * Every mutation that changes the session invalidates this query, so the router's guards and the
 * shell re-read it rather than keeping their own copy.
 */

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { auth } from "@/api/auth";
import type { components } from "@/api/schema";

export type Me = components["schemas"]["MeOut"];

/** Thrown when `/me` says there is no session, so guards can tell 401 from a real failure. */
export class NotAuthenticated extends Error {
  constructor() {
    super("not authenticated");
    this.name = "NotAuthenticated";
  }
}

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: async (): Promise<Me> => {
    const { data, error, response } = await api.GET("/api/v1/me");
    if (response.status === 401) throw new NotAuthenticated();
    if (error || !data) throw new Error("could not load the signed-in user");
    return data;
  },
  // The session does not change behind the app's back, and every mutation invalidates it.
  staleTime: 5 * 60_000,
  retry: false,
});

/**
 * Prime Django's CSRF cookie. Nothing can POST until a response has set it, so this runs once
 * before the auth pages render.
 */
export const authConfigQuery = queryOptions({
  queryKey: ["auth", "config"],
  queryFn: () => auth.config(),
  staleTime: Infinity,
  retry: 1,
});

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // allauth answers a successful sign-out with 401, which this client treats as an error.
      await auth.signOut().catch(() => undefined);
    },
    onSettled: () => {
      // `clear`, not `invalidateQueries`: invalidating marks data stale but keeps it, and the
      // route guards read the cache through `ensureQueryData`, which returns whatever is there
      // without refetching. A signed-out user would be bounced straight back into the app.
      // Dropping everything is also right on its own terms — nothing cached belongs to whoever
      // signs in next, which matters on a shared phone.
      queryClient.clear();
    },
  });
}

/** Forget everything cached for whoever was here before, then read the new session. */
export async function sessionChanged(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.clear();
  await queryClient.fetchQuery(meQuery);
}
